import { isTagCriteria } from "./criteriaFilterUtils";
import { POSITIVE_TO_NEGATION_MAP } from "./notificationOperatorNegation";

import type { CriteriaFilterValue } from "@stacklet/ui";
import type { FilterElementInput } from "app/utils/filters";

interface CriteriaFiltersBySection {
  providerFilters: CriteriaFilterValue[];
  accountFilters: CriteriaFilterValue[];
  resourceFilters: CriteriaFilterValue[];
  policyFilters: CriteriaFilterValue[];
  bindingFilters: CriteriaFilterValue[];
}

type FilterSingle = NonNullable<FilterElementInput["single"]>;

const ACCOUNT_CRITERIA = new Set([
  "account-name",
  "account-group-name",
  "account-tag",
]);
const RESOURCE_CRITERIA = new Set(["resource-type", "resource-tag"]);
const POLICY_CRITERIA = new Set([
  "policy-name",
  "action-required",
  "policy-category",
  "policy-severity",
  "policy-collection-name",
]);
const BINDING_CRITERIA = new Set(["binding-uuid"]);
const PROVIDER_CRITERIA = new Set(["provider"]);

/**
 * Returns true when every operand is a single(equals) and all share the same name.
 * Used to detect the OR(equals, equals, ...) structure produced by "is one of".
 */
function isEqualsGroupForSameName(
  operands: readonly FilterElementInput[],
): boolean {
  if (operands.length === 0) return false;
  const firstName = operands[0].single?.name;
  return (
    firstName != null &&
    operands.every(
      (o) =>
        (o.single?.operator == null ||
          o.single.operator.trim().toLowerCase() === "equals") &&
        o.single?.name === firstName,
    )
  );
}

/**
 * Collapses an OR(equals, equals, ...) group into a single FilterSingle with a
 * comma-joined value and the given operator ("is one of" or "is not one of").
 *
 * Precondition: operands must be non-empty (guaranteed by isEqualsGroupForSameName).
 */
function collapseEqualsGroup(
  operands: readonly FilterElementInput[],
  operator: string,
): FilterSingle {
  const name = operands[0].single?.name ?? "";
  const value = operands
    .flatMap((o) => (o.single ? getSingleValues(o.single) : []))
    .join(",");
  return { name, operator, value };
}

/**
 * Recursively extract all single filters from a FilterElementInput at any depth.
 *
 * Handles four structures:
 * - Plain single → returned as-is
 * - NOT(single) → scalar negating operators (does-not-contain etc.), reconstructed
 *   using POSITIVE_TO_NEGATION_MAP.
 * - OR(equals, equals, ...) → reconstructed as is-one-of with comma-joined value
 * - NOT(OR(equals, equals, ...)) → reconstructed as is-not-one-of
 */
function extractSingles(
  filter: FilterElementInput | null | undefined,
): FilterSingle[] {
  if (!filter) return [];
  if (filter.single) return [filter.single];
  if (filter.multiple?.operands) {
    const { operator, operands } = filter.multiple;

    if (operator?.toUpperCase() === "NOT" && operands.length === 1) {
      const inner = operands[0];

      // NOT(single) — scalar negating operators (e.g. NOT(single(contains))).
      // "is one of" is a frontend-only concept always serialized to OR(equals,...),
      // so it will never appear as a raw operator in server data.
      if (inner.single) {
        const rawOp = (
          inner.single.operator?.trim().toLowerCase() ?? ""
        ).replace(/-/g, " ");
        const negatedOp = POSITIVE_TO_NEGATION_MAP[rawOp];
        if (negatedOp !== undefined) {
          return [{ ...inner.single, operator: negatedOp }];
        }
        console.warn(
          `[notificationRuleFilterToCriteriaFilters] NOT-wrapped filter has unrecognized operator "${inner.single.operator}". Skipping filter to avoid incorrect semantics. To add support, add the operator to POSITIVE_TO_NEGATION_MAP in notificationOperatorNegation.ts.`,
        );
        return [];
      }

      // NOT(OR(equals, equals, ...)) — new-format is-not-one-of
      if (
        inner.multiple?.operator?.toUpperCase() === "OR" &&
        isEqualsGroupForSameName(inner.multiple.operands)
      ) {
        return [collapseEqualsGroup(inner.multiple.operands, "is not one of")];
      }

      // Unrecognized NOT structure — skip rather than fall through to the generic
      // flatMap path, which would strip the NOT and produce filters with the wrong sign.
      console.warn(
        `[notificationRuleFilterToCriteriaFilters] Unrecognized NOT structure. Skipping to avoid dropping negation semantics.`,
      );
      return [];
    }

    // OR(equals, equals, ...) — new-format is-one-of
    if (
      operator?.toUpperCase() === "OR" &&
      isEqualsGroupForSameName(operands)
    ) {
      return [collapseEqualsGroup(operands, "is one of")];
    }

    return operands.flatMap((o) => extractSingles(o));
  }
  return [];
}

function criteriaBase(name: string): string {
  return name.split(":")[0];
}

function splitValues(value?: string | null): { id: string; name: string }[] {
  const v = value?.trim();
  if (!v) return [];
  return v
    .split(",")
    .map((part) => part.trim())
    .filter(Boolean)
    .map((id) => ({ id, name: id }));
}

function getSingleValues(single: FilterSingle): string[] {
  const valuesFromSingle = Array.isArray(single.values)
    ? single.values.flatMap((value) => (value != null ? [value] : []))
    : [];
  if (valuesFromSingle.length > 0) {
    return valuesFromSingle;
  }
  return single.value != null ? [single.value] : [];
}

function getSingleValue(single: FilterSingle): string | null | undefined {
  const values = getSingleValues(single);
  if (values.length === 0) {
    return undefined;
  }
  return values.join(",");
}

function normalizeOperatorFromApi(
  operator?: string | null,
): string | undefined {
  const normalized = operator?.trim();
  if (!normalized) {
    return undefined;
  }
  return normalized.toLowerCase().replace(/\s+/g, "-");
}

/**
 * Decodes the tag encoding from a single filter (from criteriaFilterValuesToFilterElement).
 * Tag-only: \{ name: "resource-tag"|"account-tag", value: "\<tagKey\>" \}
 * Tag+value: \{ name: "resource-tag:\<tagKey\>"|"account-tag:\<tagKey\>", operator, value: "\<csvValues\>" \}
 */
function decodeTagSingle(single: FilterSingle): CriteriaFilterValue | null {
  const rawName = single.name?.trim();
  if (!rawName) return null;
  const baseName = rawName.split(":")[0];
  if (!isTagCriteria(baseName)) return null;

  const hasColon = rawName.includes(":");
  let tagKey: string;
  let valuesStr: string | null | undefined;
  let operator: string | null | undefined;
  const singleValue = getSingleValue(single);

  if (hasColon) {
    const colonIndex = rawName.indexOf(":");
    tagKey = rawName.slice(colonIndex + 1).trim();
    valuesStr = singleValue;
    operator = normalizeOperatorFromApi(single.operator);
  } else {
    tagKey = singleValue?.trim() ?? "";
    valuesStr = undefined;
    operator = undefined;
  }

  return {
    criteria: baseName,
    name: tagKey,
    operator: operator ?? undefined,
    value: splitValues(valuesStr),
  };
}

function singleToCriteriaFilterValue(
  single: FilterSingle,
): CriteriaFilterValue {
  const tagCv = decodeTagSingle(single);
  if (tagCv !== null) return tagCv;

  return {
    criteria: single.name,
    name: single.name,
    operator: normalizeOperatorFromApi(single.operator) ?? "equals",
    value: splitValues(getSingleValue(single)),
  };
}

export function notificationRuleCombinedFilterToCriteriaFilters(
  combinedFilter: FilterElementInput | null | undefined,
): CriteriaFiltersBySection {
  const result: CriteriaFiltersBySection = {
    providerFilters: [],
    accountFilters: [],
    resourceFilters: [],
    policyFilters: [],
    bindingFilters: [],
  };

  const singles = extractSingles(combinedFilter);

  for (const single of singles) {
    const name = single.name?.trim();
    if (!name) continue;

    const cv = singleToCriteriaFilterValue(single);
    const base = criteriaBase(cv.criteria);

    if (PROVIDER_CRITERIA.has(base)) result.providerFilters.push(cv);
    else if (ACCOUNT_CRITERIA.has(base)) result.accountFilters.push(cv);
    else if (RESOURCE_CRITERIA.has(base)) result.resourceFilters.push(cv);
    else if (POLICY_CRITERIA.has(base)) result.policyFilters.push(cv);
    else if (BINDING_CRITERIA.has(base)) result.bindingFilters.push(cv);
  }

  return result;
}

export function notificationRuleFilterJSONToCriteriaFilters(
  filterJSON: string,
): CriteriaFiltersBySection {
  try {
    const parsed = JSON.parse(filterJSON) as FilterElementInput;

    return notificationRuleCombinedFilterToCriteriaFilters(parsed);
  } catch {
    return notificationRuleCombinedFilterToCriteriaFilters(null);
  }
}
